import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/components/AuthProvider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface SendToPayArcButtonProps {
  merchantCode: string;
  documents: { [key: string]: any[] };
  disabled?: boolean;
  onSuccess?: () => void;
}

export function SendToPayArcButton({ 
  merchantCode, 
  documents, 
  disabled = false,
  onSuccess 
}: SendToPayArcButtonProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<{
    submitted: boolean;
    submittedAt: string | null;
  } | null>(null);

  // Check if documents have already been submitted
  const checkSubmissionStatus = async () => {
    try {
      const token = await (user as any)?.getIdToken();
      const response = await fetch(`/api/payarc/documents?merchantCode=${merchantCode}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const status = await response.json();
        setSubmissionStatus(status);
        return status;
      }
    } catch (error) {
      console.error('Error checking submission status:', error);
    }
    return null;
  };

  // Convert file URLs to base64
  const convertToBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting file to base64:', error);
      throw error;
    }
  };

  const handleSendToPayArc = async () => {
    setShowConfirmDialog(false);
    setIsLoading(true);

    try {
      // Check if already submitted
      const status = await checkSubmissionStatus();
      if (status?.documentsSubmitted) {
        toast({
          title: 'Documents Already Submitted',
          description: `Documents were already sent to PayArc on ${new Date(status.submittedAt).toLocaleDateString()}`,
          variant: 'default',
        });
        return;
      }

      // Prepare documents for submission
      const documentsToSend = [];
      
      if (!documents) {
        toast({
          title: 'No Documents Available',
          description: 'No documents to send to PayArc',
          variant: 'destructive'
        });
        setIsLoading(false);
        return;
      }
      
      for (const [docType, files] of Object.entries(documents)) {
        if (Array.isArray(files) && files.length > 0) {
          for (const file of files) {
            const fileUrl = typeof file === 'string' ? file : file.url;
            const fileName = typeof file === 'string' 
              ? `${docType}_${Date.now()}.pdf` 
              : file.name || `${docType}_${Date.now()}.pdf`;
            
            if (fileUrl) {
              const base64Data = await convertToBase64(fileUrl);
              documentsToSend.push({
                type: docType,
                name: fileName,
                data: base64Data
              });
            }
          }
        }
      }

      if (documentsToSend.length === 0) {
        toast({
          title: 'No Documents to Send',
          description: 'Please upload documents before sending to PayArc',
          variant: 'destructive',
        });
        return;
      }

      // Send documents to PayArc
      const token = await (user as any)?.getIdToken();
      const response = await fetch('/api/payarc/documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          merchantCode,
          documents: documentsToSend
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send documents');
      }

      toast({
        title: 'Documents Sent Successfully',
        description: `${documentsToSend.length} document(s) have been sent to PayArc for processing`,
      });

      // Update submission status
      setSubmissionStatus({
        submitted: true,
        submittedAt: new Date().toISOString()
      });

      onSuccess?.();

    } catch (error) {
      console.error('Error sending documents to PayArc:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send documents to PayArc',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if there are any documents to send
  const hasDocuments = documents && Object.values(documents).some(
    files => Array.isArray(files) && files.length > 0
  );

  return (
    <>
      <Button
        onClick={() => setShowConfirmDialog(true)}
        disabled={disabled || isLoading || !hasDocuments || submissionStatus?.submitted}
        className="gap-2"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : submissionStatus?.submitted ? (
          <>
            <CheckCircle className="h-4 w-4" />
            Sent to PayArc
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            Send to PayArc
          </>
        )}
      </Button>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Documents to PayArc</DialogTitle>
            <DialogDescription>
              Are you sure you want to send these documents to PayArc for underwriting?
            </DialogDescription>
          </DialogHeader>
          
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Once sent, these documents will be reviewed by PayArc's underwriting team. 
              This action cannot be undone.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <p className="text-sm font-medium">Documents to be sent:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              {documents && Object.entries(documents).map(([type, files]) => {
                if (Array.isArray(files) && files.length > 0) {
                  return (
                    <li key={type}>
                      • {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {files.length} file(s)
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSendToPayArc}>
              Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}